interface TopicIconProps {
	topic: string
	size?: string
}

// Hand-drawn SVG icons for the topic grid. They're inline (not <img>) so
// fill="currentColor" / stroke="currentColor" inherits the button's text
// color — which Bootstrap already adapts for light/dark mode — so no
// per-theme CSS is needed. viewBox is 512 to match the brand logo's
// coordinate space.
//
// House style, so the set reads as one family: stroked outlines on a 512
// grid, stroke-width 34, round caps and joins, no fill except small solid
// accents (dots, the capital star). Keep new icons to that recipe.
export function TopicIcon({ topic, size = "2.5rem" }: TopicIconProps) {
	const attrs = { width: size, height: size, viewBox: "0 0 512 512" as const }
	const stroke = {
		fill: "none",
		stroke: "currentColor",
		"stroke-width": 34,
		"stroke-linecap": "round" as const,
		"stroke-linejoin": "round" as const,
	}
	switch (topic) {
		case "math":
			// The four operators in a 2x2 grid: + − × ÷
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M100 152 H204 M152 100 V204" />
						<path d="M308 152 H412" />
						<path d="M116 324 L188 396 M188 324 L116 396" />
						<path d="M308 360 H412" />
					</g>
					<g fill="currentColor">
						<circle cx="360" cy="306" r="20" />
						<circle cx="360" cy="414" r="20" />
					</g>
				</svg>
			)
		case "numberwords":
			// A numeral above the two bars that stand in for its written-out
			// name — the topic is exactly that pairing.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M176 96 H336 L246 274" />
						<path d="M104 362 H408" />
						<path d="M152 454 H360" />
					</g>
				</svg>
			)
		case "romannumerals":
			// "IV" — the numeral that teaches the subtractive rule, and the
			// only two letterforms that stay legible at 40px.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M136 130 V382 M96 130 H176 M96 382 H176" />
						<path d="M264 130 L336 382 L408 130" />
					</g>
				</svg>
			)
		case "elements":
			// An atom: nucleus with two crossed orbits.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<ellipse
							cx="256"
							cy="256"
							rx="176"
							ry="72"
							transform="rotate(-30 256 256)"
						/>
						<ellipse
							cx="256"
							cy="256"
							rx="176"
							ry="72"
							transform="rotate(30 256 256)"
						/>
					</g>
					<circle cx="256" cy="256" r="34" fill="currentColor" />
				</svg>
			)
		case "capitals":
			// Map pin with a star — the cartographic mark for a capital city.
			return (
				<svg {...attrs} aria-hidden="true">
					<path
						{...stroke}
						d="M256 452 C 196 372, 118 300, 118 214 C 118 138, 180 78, 256 78 C 332 78, 394 138, 394 214 C 394 300, 316 372, 256 452 Z"
					/>
					<path
						fill="currentColor"
						d="M256 148 L269 182 L307 184 L277 207 L288 244 L256 222 L224 244 L235 207 L205 184 L243 182 Z"
					/>
				</svg>
			)
		case "flags":
			// Flag on a pole, banner waving.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M140 74 V 446" />
						<path d="M140 116 C 212 76, 284 156, 356 118 C 384 103, 408 96, 428 90 L 428 254 C 408 260, 384 267, 356 282 C 284 320, 212 240, 140 280 Z" />
					</g>
				</svg>
			)
		case "shapes":
			// An anonymous landmass outline — what the topic actually shows.
			return (
				<svg {...attrs} aria-hidden="true">
					<path
						{...stroke}
						d="M130 208 C 140 146, 214 110, 272 138 C 316 159, 366 124, 404 158 C 440 190, 424 236, 386 252 C 420 274, 404 330, 356 352 C 318 370, 300 402, 258 392 C 206 380, 176 348, 156 312 C 138 280, 118 254, 130 208 Z"
					/>
				</svg>
			)
		case "continents":
			// Globe: outline, one meridian, two parallels.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<circle cx="256" cy="256" r="168" />
						<path d="M256 88 C 198 140, 198 372, 256 424 C 314 372, 314 140, 256 88 Z" />
						<path d="M102 200 H410 M102 312 H410" />
					</g>
				</svg>
			)
		case "science":
			// A conical flask — the one piece of glassware that reads at
			// 40px.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M208 72 V216 L112 400 A32 32 0 0 0 140 448 H372 A32 32 0 0 0 400 400 L304 216 V72" />
						<path d="M184 72 H328" />
						<path d="M158 320 H354" />
					</g>
				</svg>
			)
		case "history":
			// An hourglass: time, without a date or a place in it.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M136 72 H376" />
						<path d="M136 440 H376" />
						<path d="M168 72 V150 L256 256 L344 150 V72" />
						<path d="M168 440 V362 L256 256 L344 362 V440" />
					</g>
				</svg>
			)
		case "spelling":
			// A word with the wrong letter struck through — the topic is
			// picking the right spelling out of four.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M96 200 V330 M96 330 H176" />
						<path d="M240 190 V340" />
						<path d="M320 200 L368 300 L416 200" />
						<path d="M64 265 H448" />
					</g>
				</svg>
			)
		case "borders":
			// Two territories meeting along a shared edge — the border is
			// the one line drawn heavier by being unbroken.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M256 72 C 214 148, 298 208, 256 268 C 214 328, 292 372, 256 440" />
						<path d="M256 72 H100 V440 H256" />
						<path d="M256 72 H412 V440 H256" />
					</g>
				</svg>
			)
		case "countrysize":
			// Three nested squares: the topic is comparing areas, so the
			// icon is the comparison itself rather than a map.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<rect x="72" y="72" width="368" height="368" rx="28" />
						<rect x="150" y="230" width="130" height="130" rx="18" />
						<rect x="320" y="300" width="60" height="60" rx="12" />
					</g>
				</svg>
			)
		case "indianstates":
			// The tricolour reduced to its geometry: three bands and the
			// chakra. Country outlines were tried first and lost — at the
			// 2.5rem the grid renders, a 34-wide stroke swallows every
			// feature that makes India or the US recognizable, so both
			// state topics identify their country by its flag instead.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<rect x="64" y="132" width="384" height="248" rx="32" />
						<path d="M64 215 H448 M64 297 H448" />
					</g>
					<circle cx="256" cy="256" r="28" fill="currentColor" />
				</svg>
			)
		case "usstates":
			// Stars and stripes, same frame as the Indian flag above: the
			// star in the canton and the stripes are what tell them apart
			// at icon size.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<rect x="64" y="132" width="384" height="248" rx="32" />
						<path d="M236 200 H448 M236 262 H448 M64 322 H448" />
					</g>
					<path
						fill="currentColor"
						d="M150 167 L160 194 L191 195 L167 214 L176 243 L150 226 L124 243 L133 214 L109 195 L140 194 Z"
					/>
				</svg>
			)
		case "animals":
			// Bear face: head, two ears, eyes and a snout.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<circle cx="152" cy="152" r="60" />
						<circle cx="360" cy="152" r="60" />
						<circle cx="256" cy="296" r="150" />
					</g>
					<g fill="currentColor">
						<circle cx="204" cy="276" r="20" />
						<circle cx="308" cy="276" r="20" />
						<circle cx="256" cy="342" r="26" />
					</g>
				</svg>
			)
		case "counting":
			// One, then two, then three counters.
			return (
				<svg {...attrs} aria-hidden="true">
					<g fill="currentColor">
						<circle cx="256" cy="112" r="52" />
						<circle cx="178" cy="256" r="52" />
						<circle cx="334" cy="256" r="52" />
						<circle cx="100" cy="400" r="52" />
						<circle cx="256" cy="400" r="52" />
						<circle cx="412" cy="400" r="52" />
					</g>
				</svg>
			)
		case "countingwords":
			// Counting's dots over the two bars numberwords uses for a written
			// word: things above, their name written out below.
			return (
				<svg {...attrs} aria-hidden="true">
					<g fill="currentColor">
						<circle cx="124" cy="140" r="52" />
						<circle cx="256" cy="140" r="52" />
						<circle cx="388" cy="140" r="52" />
					</g>
					<g {...stroke}>
						<path d="M92 314 H420" />
						<path d="M148 404 H364" />
					</g>
				</svg>
			)
		case "spellnumbers":
			// A letter tile dropping into the gap between two written-out
			// halves — the topic is filling in the blank. Anything finer (a
			// numeral, actual letterforms) is a blob at 40px.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<rect x="181" y="30" width="150" height="150" rx="30" />
						<path d="M256 216 V296" />
						<path d="M204 252 L256 306 L308 252" />
						<path d="M56 402 H176 M336 402 H456" />
					</g>
				</svg>
			)
		case "buildnumbers":
			// Three loose letter tiles, one already lifted into place above the
			// other two — the topic is assembling a word out of a keypad.
			// Squares only: letterforms are a blur at 40px.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<rect x="181" y="46" width="150" height="150" rx="30" />
						<rect x="46" y="316" width="150" height="150" rx="30" />
						<rect x="316" y="316" width="150" height="150" rx="30" />
					</g>
				</svg>
			)
		case "firstmath":
			// Only the two operators this topic uses — the plain Math icon
			// shows all four.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M170 158 H342 M256 72 V244" />
						<path d="M170 368 H342" />
					</g>
				</svg>
			)
		case "opposites":
			// The topic's own first example: a big circle and a small one.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<circle cx="176" cy="256" r="104" />
						<circle cx="392" cy="256" r="44" />
					</g>
				</svg>
			)
		case "letters":
			// A capital A.
			return (
				<svg {...attrs} aria-hidden="true">
					<g {...stroke}>
						<path d="M150 400 L256 112 L362 400" />
						<path d="M192 302 H320" />
					</g>
				</svg>
			)
		default:
			// Fallback for topics without an icon yet.
			return (
				<svg {...attrs} aria-hidden="true">
					<path
						{...stroke}
						d="M186 196 C 186 146, 224 118, 264 122 C 310 127, 336 168, 314 206 C 296 237, 256 244, 256 288"
					/>
					<circle cx="256" cy="366" r="24" fill="currentColor" />
				</svg>
			)
	}
}
